import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import {
  StatutPaieResponse,
} from './payroll-period-alert.service';

@Component({
  selector: 'app-payroll-period-alert',
  standalone: true,

  template: `
    <section
      class="
        w-full
        shrink-0
        border-b
        border-secondary-500
        bg-secondary-50
        transition-all
      "
      [class.border-2]="deadlineUrgent()"
      role="alert"
      aria-live="polite"
    >
      <div
        class="
          flex
          items-center
          justify-between
          gap-4
          px-6
          py-3
        "
      >
        <div
          class="
            flex
            min-w-0
            items-center
            gap-3
          "
        >
          <!-- Alert icon -->
          <div
            class="
              flex
              h-9
              w-9
              shrink-0
              items-center
              justify-center
              rounded-badge
              border
              border-secondary-500
              text-secondary-500
            "
            [class.bg-secondary-500]="deadlineUrgent()"
            [class.bg-secondary-50]="!deadlineUrgent()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="
                  M10.29 3.86
                  1.82 18
                  a2 2 0 0 0 1.71 3
                  h16.94
                  a2 2 0 0 0 1.71-3
                  L13.71 3.86
                  a2 2 0 0 0-3.42 0Z
                "
              />

              <line
                x1="12"
                x2="12"
                y1="9"
                y2="13"
              />

              <line
                x1="12"
                x2="12.01"
                y1="17"
                y2="17"
              />
            </svg>
          </div>

          <!-- Message -->
          <div class="min-w-0">

            <div
              class="
                flex
                flex-wrap
                items-center
                gap-2
              "
            >
              <span
                class="
                  font-semibold
                  text-secondary-500
                "
              >
                Clôture de la période de paie requise
              </span>

              @if (deadlineUrgent()) {
                <span
                  class="
                    rounded-badge
                    bg-secondary-500
                    px-2.5
                    py-1
                    text-xs
                    font-semibold
                  "
                >
                  {{ urgencyLabel() }}
                </span>
              }
            </div>

            <div
              class="
                mt-1
                flex
                flex-wrap
                items-center
                gap-x-1
                gap-y-1
                text-sm
              "
            >
              <span>
                La période de paie de
              </span>

              <span class="font-semibold">
                {{ periodLabel() }}
              </span>

              <span>
                n'est pas encore clôturée.
              </span>

              @if (deadlineLabel()) {
                <span>
                  La date limite de clôture est le
                </span>

                <span
                  class="
                    font-semibold
                    text-secondary-500
                  "
                >
                  {{ deadlineLabel() }}.
                </span>
              }

              <span>
                Veuillez finaliser le traitement
                de la paie et clôturer la période.
              </span>
            </div>

          </div>
        </div>

        <!-- Right-side status -->
        <div class="hidden shrink-0 md:block">

          @if (deadlineUrgent()) {

            <div
              class="
                rounded-badge
                bg-secondary-500
                px-3
                py-1.5
                text-xs
                font-semibold
              "
            >
              {{ remainingTimeLabel() }}
            </div>

          } @else {

            <div
              class="
                rounded-badge
                border
                border-secondary-500
                bg-secondary-50
                px-3
                py-1.5
                text-xs
                font-medium
                text-secondary-500
              "
            >
              Action requise
            </div>

          }

        </div>

      </div>
    </section>
  `,

  styles: [`
    :host {
      display: block;
      width: 100%;
      flex-shrink: 0;
    }
  `],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollPeriodAlertComponent {

  readonly salaryState =
    input.required<StatutPaieResponse>();

  /**
   * Example:
   *
   * mois = 8
   * annee = 2026
   *
   * => "Août 2026"
   */
  readonly periodLabel = computed(() => {

    const state = this.salaryState();

    const date = new Date(
      state.annee,
      state.mois - 1,
      1,
    );

    const label =
      new Intl.DateTimeFormat(
        'fr-FR',
        {
          month: 'long',
          year: 'numeric',
        },
      ).format(date);

    return this.capitalize(label);
  });

  /**
   * Number of calendar days remaining before
   * dateLimiteCloture.
   *
   * Examples:
   *
   * 5  => five days remaining
   * 2  => urgent
   * 1  => urgent
   * 0  => today
   * -1 => already exceeded
   */
  readonly remainingDays = computed<
    number | null
  >(() => {

    const deadline =
      this.salaryState().dateLimiteCloture;

    if (!deadline) {
      return null;
    }

    const deadlineDate =
      new Date(deadline);

    if (
      Number.isNaN(
        deadlineDate.getTime(),
      )
    ) {
      return null;
    }

    const now = new Date();

    const millisecondsPerDay =
      1000 * 60 * 60 * 24;

    return Math.ceil(
      (
        deadlineDate.getTime() -
        now.getTime()
      ) /
      millisecondsPerDay,
    );
  });

  /**
   * Urgent when the deadline:
   *
   * - is in <= 2 days;
   * - is today;
   * - has already passed.
   */
  readonly deadlineUrgent = computed(() => {

    const days =
      this.remainingDays();

    if (days === null) {
      return false;
    }

    return days <= 2;
  });

  readonly deadlineLabel = computed(() => {

    const deadline =
      this.salaryState().dateLimiteCloture;

    if (!deadline) {
      return null;
    }

    const date =
      new Date(deadline);

    if (
      Number.isNaN(
        date.getTime(),
      )
    ) {
      return null;
    }

    return new Intl.DateTimeFormat(
      'fr-FR',
      {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
    ).format(date);
  });

  readonly urgencyLabel = computed(() => {

    const days =
      this.remainingDays();

    if (days === null) {
      return '';
    }

    if (days < 0) {
      return 'Échéance dépassée';
    }

    if (days === 0) {
      return 'Échéance aujourd’hui';
    }

    return 'Échéance imminente';
  });

  readonly remainingTimeLabel = computed(() => {

    const days =
      this.remainingDays();

    if (days === null) {
      return 'Action requise';
    }

    if (days < 0) {
      return 'Date limite dépassée';
    }

    if (days === 0) {
      return 'Aujourd’hui';
    }

    if (days === 1) {
      return '1 jour restant';
    }

    return `${days} jours restants`;
  });

  private capitalize(
    value: string,
  ): string {

    if (!value) {
      return value;
    }

    return (
      value.charAt(0).toUpperCase() +
      value.slice(1)
    );
  }
}